/**
 * 📝 EmailDraftsContext
 * 
 * Contexto para compartilhar informações de rascunhos de email entre componentes.
 * 
 * @author OmniPlay Email Channel
 * @date 2025-12-22
 */

import React, { createContext, useState, useEffect, useCallback, useContext } from "react";
import api from "../../services/api";
import { AuthContext } from "../Auth/AuthContext";

export const EmailDraftsContext = createContext({
  ticketIdsWithDraft: new Set(),
  hasDraft: () => false,
  addDraftTicketId: () => {},
  removeDraftTicketId: () => {},
  refresh: () => {},
});

export const EmailDraftsProvider = ({ children }) => {
  const [ticketIdsWithDraft, setTicketIdsWithDraft] = useState(new Set());
  const { user } = useContext(AuthContext);

  // Buscar ticketIds com rascunho
  const fetchDraftTicketIds = useCallback(async () => {
    if (!user?.companyId) return;
    
    try {
      const { data } = await api.get("/api/email-drafts/ticket-ids");
      setTicketIdsWithDraft(new Set(data.ticketIds || []));
    } catch (error) {
      // Silenciar erro - pode não ter o endpoint ainda
      console.debug("Erro ao buscar rascunhos:", error);
    }
  }, [user?.companyId]);

  // Verificar se ticket tem rascunho
  const hasDraft = useCallback((ticketId) => {
    return ticketIdsWithDraft.has(Number(ticketId));
  }, [ticketIdsWithDraft]);

  // Adicionar ticketId ao set
  const addDraftTicketId = useCallback((ticketId) => {
    setTicketIdsWithDraft(prev => new Set([...prev, Number(ticketId)]));
  }, []);

  // Remover ticketId do set
  const removeDraftTicketId = useCallback((ticketId) => {
    setTicketIdsWithDraft(prev => {
      const newSet = new Set(prev);
      newSet.delete(Number(ticketId));
      return newSet;
    });
  }, []);

  // Buscar ao montar e periodicamente
  useEffect(() => {
    fetchDraftTicketIds();
    
    // Atualizar a cada 60 segundos
    const interval = setInterval(fetchDraftTicketIds, 60000);
    return () => clearInterval(interval);
  }, [fetchDraftTicketIds]);

  return (
    <EmailDraftsContext.Provider
      value={{
        ticketIdsWithDraft,
        hasDraft,
        addDraftTicketId,
        removeDraftTicketId,
        refresh: fetchDraftTicketIds,
      }}
    >
      {children}
    </EmailDraftsContext.Provider>
  );
};

export const useEmailDrafts = () => useContext(EmailDraftsContext);

